import { Injectable } from '@angular/core';
import {from, Observable, of, throwError} from 'rxjs';
import {catchError, map, tap} from 'rxjs/operators';
import {classToPlain, plainToClass} from 'class-transformer';
import {db} from "../../dexie/db";

import { SOPutSimpleAttributePermissionRule } from './so-put-simple-attribute-permission-rule';
import { SOGetSimpleAttributePermissionRule } from './so-get-simple-attribute-permission-rule';
import { SOPostSimpleAttributePermissionRule } from './so-post-simple-attribute-permission-rule';
import { SODeleteSimpleAttributePermissionRule } from './so-delete-simple-attribute-permission-rule';
import {SORespSimpleAttributePermissionRule} from './so-resp-simple-attribute-permission-rule';
import {SOPluralSimpleAttributePermissionRule} from './so-plural-simple-attribute-permission-rule';

import {MMEntityService} from 'lionheartland';
import {EntityCoreProtocol} from 'lionheartland';
import {MMEntityServiceBase} from 'lionheartland';
import {HttpHeaders} from "@angular/common/http";

export class EOSimpleAttributePermissionRuleService extends MMEntityServiceBase implements MMEntityService {

  constructor(
    public entityCore: EntityCoreProtocol,
  ) {
    super('simple_attribute_permission_rule', entityCore);
  }

  /** GET SimpleAttributePermissionRule by id. Will 404 if id not found */
  get(request: SOGetSimpleAttributePermissionRule): Observable<SOPluralSimpleAttributePermissionRule> {
    return this.httpClient.get<SOPluralSimpleAttributePermissionRule>(this.makeGetUri(request), this.httpOptions).pipe(
      map(resp => plainToClass(SOPluralSimpleAttributePermissionRule, resp)),
      tap(async (response: SOPluralSimpleAttributePermissionRule) => {
        this.log(`fetched SimpleAttributePermissionRule`);
        // After a successful server response, update IndexedDB
        const activities = response.toSimpleAttributePermissionRules(); // Convert your response to an array of SimpleAttributePermissionRule instances
        const promises = activities.map(simpleAttributePermissionRule => db.simpleAttributePermissionRules.put(simpleAttributePermissionRule));
        await Promise.all(promises); // Wait for all the IndexedDB update operations to complete
      }),
      catchError(this.handleError<SOPluralSimpleAttributePermissionRule>(`get SimpleAttributePermissionRule`))
    );
  }

    /** GETCACHE for initial display with CacheQueryProtocol */
    getCache(cacheQuery: any): Observable<SOPluralSimpleAttributePermissionRule> {
        console.log("GETCACHE", cacheQuery);

        if (cacheQuery.filterKey === 'simpleAttributePermissionRule' && cacheQuery.filterValue && cacheQuery.filterOrganization)
        {
            console.log("cache filtering based on parent and organization")
            return from(db.simpleAttributePermissionRules.where('parentUid').equals(cacheQuery.filterValue).and(item => item.organizationUid === cacheQuery.filterOrganization.uid).toArray()).pipe(
                map(simpleAttributePermissionRulesArray => this.processSimpleAttributePermissionRules(simpleAttributePermissionRulesArray)),
                catchError(error => this.handleQueryError(error))
            );
        }
        else if (cacheQuery.filterKey + "s" == "simpleAttribute" && cacheQuery.filterValue && cacheQuery.filterOrganization)
        {
            console.log("cache filtering based on relationship and organization")
            return from(db.simpleAttributePermissionRules.where('simpleAttributeUid').equals(cacheQuery.filterValue).and(item => item.organizationUid === cacheQuery.filterOrganization.uid).toArray()).pipe(
                map(simpleAttributePermissionRulesArray => this.processSimpleAttributePermissionRules(simpleAttributePermissionRulesArray)),
                catchError(error => this.handleQueryError(error))
            );
        }        else if (cacheQuery.filterKey + "s" == "simpleAttributePermission" && cacheQuery.filterValue && cacheQuery.filterOrganization)
        {
            console.log("cache filtering based on relationship and organization")
            return from(db.simpleAttributePermissionRules.where('simpleAttributePermissionUid').equals(cacheQuery.filterValue).and(item => item.organizationUid === cacheQuery.filterOrganization.uid).toArray()).pipe(
                map(simpleAttributePermissionRulesArray => this.processSimpleAttributePermissionRules(simpleAttributePermissionRulesArray)),
                catchError(error => this.handleQueryError(error))
            );
        }        else if (cacheQuery.filterKey + "s" == "project" && cacheQuery.filterValue && cacheQuery.filterOrganization)
        {
            console.log("cache filtering based on relationship and organization")
            return from(db.simpleAttributePermissionRules.where('projectUid').equals(cacheQuery.filterValue).and(item => item.organizationUid === cacheQuery.filterOrganization.uid).toArray()).pipe(
                map(simpleAttributePermissionRulesArray => this.processSimpleAttributePermissionRules(simpleAttributePermissionRulesArray)),
                catchError(error => this.handleQueryError(error))
            );
        }        else if (cacheQuery.filterKey + "s" == "organization" && cacheQuery.filterValue && cacheQuery.filterOrganization)
        {
            console.log("cache filtering based on relationship and organization")
            return from(db.simpleAttributePermissionRules.where('organizationUid').equals(cacheQuery.filterValue).and(item => item.organizationUid === cacheQuery.filterOrganization.uid).toArray()).pipe(
                map(simpleAttributePermissionRulesArray => this.processSimpleAttributePermissionRules(simpleAttributePermissionRulesArray)),
                catchError(error => this.handleQueryError(error))
            );
        }        else if (cacheQuery.filterOrganization || cacheQuery.organization.uid)
        {
            console.log("tripped organization for cache query")
            if ( cacheQuery?.filterOrganization != null )
            {
                console.log("cache filtering based on organization CQP", cacheQuery)
                return from(db.simpleAttributePermissionRules.where('organizationUid').equals(cacheQuery.filterOrganization).toArray()).pipe(
                    map(simpleAttributePermissionRulesArray => this.processSimpleAttributePermissionRules(simpleAttributePermissionRulesArray)),
                    catchError(error => this.handleQueryError(error))
                );
            }
            else if ( cacheQuery?.organization?.uid != null )
            {
                console.log("UID:", cacheQuery.organization.uid)
                console.log("cache filtering based on organization SOGet")
                return from(db.simpleAttributePermissionRules.where('organizationUid').equals(cacheQuery.organization.uid).toArray()).pipe(
                    map(simpleAttributePermissionRulesArray => this.processSimpleAttributePermissionRules(simpleAttributePermissionRulesArray)),
                    catchError(error => this.handleQueryError(error))
                );
            }
            else
            {
                console.log("null organization")
            }
        }
        else
        {
            console.log("cache no filter")
            return from(db.simpleAttributePermissionRules.toArray()).pipe(
                map((simpleAttributePermissionRulesArray) => {
                    // Convert the array of plain objects to an array of SORespSimpleAttributePermissionRule instances
                    const sorSimpleAttributePermissionRules = simpleAttributePermissionRulesArray.map(req => plainToClass(SORespSimpleAttributePermissionRule, req));
                    // Create and return a new instance of SOPluralSimpleAttributePermissionRule
                    const pluralSimpleAttributePermissionRule = new SOPluralSimpleAttributePermissionRule();
                    pluralSimpleAttributePermissionRule.objects = sorSimpleAttributePermissionRules;
                    pluralSimpleAttributePermissionRule.nextOffset = null; // Assuming there is no pagination in cache
                    return pluralSimpleAttributePermissionRule;
                }),
                catchError(error => this.handleQueryError(error))
            );
        }
    }

    private processSimpleAttributePermissionRules(simpleAttributePermissionRulesArray) {
        const sorSimpleAttributePermissionRules = simpleAttributePermissionRulesArray.map(req => plainToClass(SORespSimpleAttributePermissionRule, req));
        const pluralSimpleAttributePermissionRule = new SOPluralSimpleAttributePermissionRule();
        pluralSimpleAttributePermissionRule.objects = sorSimpleAttributePermissionRules;
        pluralSimpleAttributePermissionRule.nextOffset = null;
        return pluralSimpleAttributePermissionRule;
    }

    private handleQueryError(error) {
        console.error('Error in query:', error);
        return throwError(error); // Return an observable error
    }

post(request: SOPostSimpleAttributePermissionRule): Observable<SORespSimpleAttributePermissionRule> {
  return this.httpClient.post<SORespSimpleAttributePermissionRule>(this.coreUrl + this.route, request, this.httpOptions).pipe(
    map(resp => plainToClass(SORespSimpleAttributePermissionRule, resp)),

    tap(async (response: SORespSimpleAttributePermissionRule) => {
      this.log(`added SimpleAttributePermissionRule w/ id=${response.uid}`);
      // After a successful server response, update IndexedDB
      console.log("here", response);
      console.log("here request", request);
      await db.simpleAttributePermissionRules.add(response.toSimpleAttributePermissionRule());
    }),
    catchError(this.handleError<SORespSimpleAttributePermissionRule>('SimpleAttributePermissionRule post'))
  );
}


  put(request: SOPutSimpleAttributePermissionRule): Observable<SORespSimpleAttributePermissionRule> {
    const uid = request.uid;
    const uri = `${this.coreUrl}${this.route}/${uid}`;
    return this.httpClient.put<SORespSimpleAttributePermissionRule>(uri, request, this.httpOptions).pipe(
      map(resp => plainToClass(SORespSimpleAttributePermissionRule, resp)),
      tap(async (response: SORespSimpleAttributePermissionRule) => {
        this.log(`edited SimpleAttributePermissionRule w/ uid=${uid}`);
        // After a successful server response, update IndexedDB
        await db.simpleAttributePermissionRules.put(response.toSimpleAttributePermissionRule());
      }),
      catchError(this.handleError<SORespSimpleAttributePermissionRule>('SimpleAttributePermissionRule put'))
    );
  }


  delete(request: SODeleteSimpleAttributePermissionRule): Observable<SORespSimpleAttributePermissionRule> {
    return this.httpClient.delete<SORespSimpleAttributePermissionRule>(this.makeDeleteUri(request), this.httpOptions).pipe(
      tap(async _ => {
        this.log(`deleted SimpleAttributePermissionRule uid=${request.uid}`);
        // After a successful server response, update IndexedDB
        await db.simpleAttributePermissionRules.delete(request.uid);
      }),
      catchError(this.handleError<SORespSimpleAttributePermissionRule>('SimpleAttributePermissionRule delete'))
    );
  }


}
